import { gql, useMutation, useQuery } from '@apollo/client';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { useContext, useEffect, useState } from 'react';
import { GoalRiskQuestionVisual } from './goalRiskQuestion.visual';

const FETCH_USER_GOAL = gql`
  query fetchUserGoal($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        goals {
          id
          riskQuestion1
        }
      }
    }
  }
`;

const FETCH_HOUSEHOLD_GOAL = gql`
  query fetchHouseholdGoal($clientGroupId: SafeString!) {
    fetchClientGroup(input: { clientGroupId: $clientGroupId }) {
      clientGroup {
        goals {
          id
          riskQuestion1
        }
      }
    }
  }
`;

const FETCH_GOAL = gql`
  query fetchGoal($goalId: ObjectID!) {
    fetchGoal(goalId: $goalId) {
      goal {
        id
        riskQuestion1
      }
    }
  }
`;

const UPDATE_GOAL = gql`
  mutation updateGoal($input: UpdateGoalInput!) {
    updateGoal(input: $input) {
      goal {
        id
      }
    }
  }
`;

export const GoalRiskQuestion = ({
  options,
  objectId,
  userId,
  onNext,
  stepLoading,
  workflowCompletion,
  objectType = 'USER',
}: {
  options: any;
  objectId: string;
  userId: string;
  onNext: () => void;
  stepLoading: boolean;
  workflowCompletion?: any;
  objectType?: string;
}) => {
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const isNewGoal = !!workflowData.currentGoalId;
  const [goalData, setGoalData] = useState<any>({
    goalId: null,
    riskQuestion1: '',
  });

  const { data, loading } = useQuery(FETCH_GOAL, {
    variables: { goalId: workflowData.currentGoalId },
    fetchPolicy: 'no-cache',
    skip: !isNewGoal,
  });

  const { data: userData } = useQuery(
    objectType === 'HOUSEHOLD' ? FETCH_HOUSEHOLD_GOAL : FETCH_USER_GOAL,
    {
      variables: {
        ...(objectType === 'HOUSEHOLD'
          ? { clientGroupId: objectId }
          : { userId }),
      },
      fetchPolicy: 'no-cache',
      skip: isNewGoal,
    },
  );

  const [updateGoal, { loading: updateGoalLoading }] = useMutation(
    UPDATE_GOAL,
    {
      onCompleted: () => {
        onNext();
      },
      variables: {
        input: {
          ...goalData,
        },
      },
    },
  );

  useEffect(() => {
    if (data) {
      setGoalData({
        goalId: data.fetchGoal.goal.id,
        // If it is a new goal, do not preselect the option used to stub out the goal creation
        riskQuestion1: isNewGoal
          ? undefined
          : data.fetchGoal.goal.riskQuestion1 || undefined,
      });
    }
  }, [data, isNewGoal]);

  useEffect(() => {
    if (userData) {
      const firstGoal =
        objectType === 'HOUSEHOLD'
          ? userData.fetchClientGroup.clientGroup.goals[0]
          : userData.fetchUser.user.goals[0];
      if (!firstGoal) {
        return;
      }
      setGoalData({
        goalId: firstGoal.id,
        riskQuestion1: firstGoal.riskQuestion1 || undefined,
      });
    }
  }, [userData, onNext, objectType]);

  useEffect(() => {
    setWorkflowData({
      ...workflowData,
      goalRiskQuestions: options.options,
      goalRiskQuestionTitle: options.title,
    });
  }, [options]);

  return (
    <GoalRiskQuestionVisual
      options={options}
      goalData={goalData}
      updateGoal={setGoalData}
      continueFunc={updateGoal}
      loading={loading || updateGoalLoading || stepLoading}
      workflowCompletion={workflowCompletion}
    />
  );
};

export default GoalRiskQuestion;
